// Display formatting helpers — ported from design's components.jsx.
// CRITICAL: amounts in production are bigint amount_minor (INR paise).
// Convert to major units (rupees) only at the display boundary, here.

export function ccySym(c: string): string {
  if (c === "USD") return "$";
  if (c === "INR") return "₹";
  return "";
}

/**
 * fmtCcy formats a number for display. INR uses 0 decimals, USD uses 2.
 * Returns "—" for null/undefined.
 */
export function fmtCcy(
  n: number | null | undefined,
  ccy: string,
): string {
  if (n == null) return "—";
  const fixed = ccy === "INR" ? 0 : 2;
  const locale = ccy === "INR" ? "en-IN" : "en-US";
  const v = Math.abs(n).toLocaleString(locale, {
    minimumFractionDigits: fixed,
    maximumFractionDigits: fixed,
  });
  return (n < 0 ? "−" : "") + v;
}

/**
 * fmtCcyMinor converts a bigint amount_minor (INR paise) to a display string.
 * Uses integer bigint arithmetic only — no float coercion (CLAUDE.md rule 6).
 * The Number() conversion is display-only and never feeds money math.
 *
 * Note: fmtCcy() is for major-unit amounts only (mock data / non-paise fields).
 * Use fmtCcyMinor() for all real bigint amount_minor values from the API.
 */
export function fmtCcyMinor(amountMinor: bigint, ccy: string): string {
  const major = amountMinor / 100n;
  const minor = amountMinor % 100n;
  const asNumber = Number(major) + Number(minor) / 100; // safe: display only, not math
  return fmtCcy(asNumber, ccy);
}

// Date locale — audit-pass-5 UI #19. Business context is Indian (INR,
// IDFC/IDFC, Indian merchants); rest of the app already uses en-IN for
// currency. Locking to en-IN here makes the day-month order match the
// rest of the operator's tools (23 May, not May 23) rather than US convention.
const DATE_LOCALE = "en-IN";

export function formatTime(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString(DATE_LOCALE, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }
  return d.toLocaleDateString(DATE_LOCALE, { month: "short", day: "numeric" });
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(DATE_LOCALE, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/**
 * maskAccount renders a bank account number for display: keep the last
 * four digits, mask everything else, group right-to-left in fours.
 *
 *   "1234567890"  → "•• •••• 7890"
 *   "12345678901" → "••• •••• 7890"  (account length preserved)
 *   "1234"        → "1234"            (≤4 chars: nothing to mask)
 *   ""            → "—"
 *
 * The operator usually needs JUST enough of the number to disambiguate two
 * rails at a glance, without the full PII spilling into screenshots or
 * screen-shares. Same visual length as the source string so spacing in the
 * column stays predictable.
 */
export function maskAccount(acct: string | null | undefined): string {
  if (acct == null || acct === "") return "—";
  const s = acct.trim();
  if (s.length <= 4) return s;
  const masked = "•".repeat(s.length - 4) + s.slice(-4);
  // Group into 4s right-to-left, like card-number formatting.
  const groups: string[] = [];
  for (let i = masked.length; i > 0; i -= 4) {
    groups.unshift(masked.slice(Math.max(0, i - 4), i));
  }
  return groups.join(" ");
}
